/**
 * Lightweight global live-counts hook.
 *
 * Primary path: WebSocket (`/ws/notifications`) — server pushes counts on
 * connect + every state change, so the UI updates instantly with zero
 * polling cost.
 *
 * Fallback: HTTP polling at a slow cadence (60s, was 20s) when the socket
 * can't connect. This keeps the app working in environments where WS is
 * blocked (some corporate proxies, restrictive privacy extensions).
 *
 * Returns: { notifications, chat, modPending, reload }
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "./api";

const FALLBACK_POLL_MS = 60_000;

function backendWsUrl() {
    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    // Convert https → wss, http → ws. Preserve host + port + path.
    const url = new URL(base);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    // Mount under /api/ so the Kubernetes ingress routes it to the backend pod.
    return `${url.origin}/api/ws/notifications`;
}

export function useLiveCounts() {
    const { user } = useAuth();
    const isMod = user && (user.role === "admin" || user.role === "mod");
    const [counts, setCounts] = useState({ notifications: 0, chat: 0, modPending: 0 });
    const wsRef = useRef(null);
    const pollRef = useRef(null);
    const aliveRef = useRef(true);

    const fetchHttp = useCallback(async () => {
        if (!user || !aliveRef.current) return;
        try {
            const requests = [
                api.get("/notifications/unread-count").then((r) => r.data?.count ?? 0).catch(() => 0),
                api.get("/chat/unread-total").then((r) => r.data?.unread_total ?? 0).catch(() => 0),
                isMod
                    ? api.get("/admin/stats").then((r) => r.data?.open_reports ?? 0).catch(() => 0)
                    : Promise.resolve(0),
            ];
            const [notifications, chat, modPending] = await Promise.all(requests);
            if (aliveRef.current) setCounts({ notifications, chat, modPending });
        } catch { /* keep last known */ }
    }, [user, isMod]);

    useEffect(() => {
        aliveRef.current = true;
        if (!user) { setCounts({ notifications: 0, chat: 0, modPending: 0 }); return; }

        let stopped = false;
        let backoff = 5000;
        const startPolling = () => {
            if (pollRef.current) return;
            fetchHttp();
            pollRef.current = setInterval(() => {
                if (document.visibilityState === "visible") fetchHttp();
            }, FALLBACK_POLL_MS);
        };
        const stopPolling = () => {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        };

        const connectWs = () => {
            try {
                const ws = new WebSocket(backendWsUrl());
                wsRef.current = ws;
                ws.onopen = () => { backoff = 5000; stopPolling(); /* WS is the primary feed now */ };
                ws.onmessage = (e) => {
                    try {
                        const data = JSON.parse(e.data || "{}");
                        if (data.type === "counts") {
                            setCounts({
                                notifications: data.notif ?? 0,
                                chat: data.dm ?? 0,
                                modPending: data.mod_pending ?? 0,
                            });
                        }
                        // 'notification' frames are fire-and-forget UI events;
                        // when one arrives, refresh counts as a side effect.
                        if (data.type === "notification") {
                            ws.send(JSON.stringify({ type: "refresh" }));
                        }
                    } catch { /* malformed frame — ignore */ }
                };
                ws.onclose = () => {
                    wsRef.current = null;
                    if (!stopped) {
                        // WS dropped — restart fallback polling and reconnect with exponential backoff
                        // (5s → 10s → … capped at 60s) so a blocked socket doesn't spam the console.
                        startPolling();
                        setTimeout(() => { if (!stopped) connectWs(); }, backoff);
                        backoff = Math.min(backoff * 2, 60_000);
                    }
                };
                ws.onerror = () => { /* let onclose handle reconnect */ };
            } catch {
                startPolling();
            }
        };

        // Kick both: polling first (so we have data immediately) then WS (will stop polling on connect).
        startPolling();
        connectWs();
        const onVisible = () => { if (document.visibilityState === "visible") fetchHttp(); };
        document.addEventListener("visibilitychange", onVisible);

        return () => {
            stopped = true;
            aliveRef.current = false;
            stopPolling();
            if (wsRef.current) { try { wsRef.current.close(); } catch { /* noop */ } wsRef.current = null; }
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [user, fetchHttp]);

    return { ...counts, reload: fetchHttp };
}
